import axios, { type InternalAxiosRequestConfig } from 'axios';
import { parseApiError } from '@/types/error';
import { logger } from './logger';

interface FallbackAxiosRequestConfig extends InternalAxiosRequestConfig {
  _fallbackRetried?: boolean;
}

/**
 * axios のエラーが再送可能な `config` を持つかを判定する型ガード。
 *
 * `error` は `unknown` 相当で、`config` の有無はエラー種別（ネットワーク / キャンセル等）で変わる。
 * `as FallbackAxiosRequestConfig` の代わりに**型ガード**で確定させる
 * （AGENTS.md 1 の代替策。`headers` の存在まで見て axios の config であることを確かめる）。
 */
const hasFallbackConfig = (error: unknown): error is { config: FallbackAxiosRequestConfig } => {
  if (typeof error !== 'object' || error === null || !('config' in error)) return false;
  const { config } = error;
  return typeof config === 'object' && config !== null && 'headers' in config && 'url' in config;
};

export const getApiEndpoint = (hostname: string = window.location.hostname): string => {
  const envEndpoint = import.meta.env.VITE_API_ENDPOINT;
  if (envEndpoint) return envEndpoint;

  // `admin` をサブドメインのラベルとしてのみ `adminapi` へ置換する
  // (admin.enet-dev... → adminapi.enet-dev... / feature-x.admin.enet-feat... → feature-x.adminapi.enet-feat...)。
  // 先頭 or ドット区切りの `admin.` だけを対象にし、ブランチ名内の "admin"
  // (例 feature-271-menu-admin の -admin 部分) は置換しない。
  const adminLabel = /(^|\.)admin\./;
  if (adminLabel.test(hostname)) {
    return `https://${hostname.replace(adminLabel, '$1adminapi.')}`;
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    logger.logWarning('ローカル環境: VITE_API_ENDPOINT 環境変数が設定されていません');
    return 'http://localhost:3000';
  }
  return '/api';
};

// Dev backend fallback URL (set by CI for feat/review builds)
const devApiEndpoint = import.meta.env.VITE_API_DEV_ENDPOINT;

// Axios instance
const apiClient = axios.create({
  baseURL: getApiEndpoint(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token provider - Auth0から設定される
let tokenProvider: (() => Promise<string>) | null = null;

export const setTokenProvider = (provider: () => Promise<string>) => {
  tokenProvider = provider;
};

// Request interceptor - 自動的にJWTトークンを追加
apiClient.interceptors.request.use(
  async (config) => {
    if (tokenProvider) {
      try {
        const token = await tokenProvider();
        if (!token) {
          throw new Error('No access token available');
        }
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        logger.logError('Failed to get access token', error);
        throw error;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - エラーハンドリング + dev backend fallback
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Fallback to dev backend on network error (feature backend not deployed)
    // axios のエラーは `unknown` 相当。`config` は再送に使うのでプロパティの有無を確認する
    // （`as FallbackAxiosRequestConfig` は形が変わっても黙って通る。AGENTS.md 1）。
    const config = hasFallbackConfig(error) ? error.config : undefined;
    if (error.code === 'ERR_NETWORK' && devApiEndpoint && config && !config._fallbackRetried) {
      logger.logWarning(`Feature backend unreachable, falling back to dev: ${devApiEndpoint}`);
      config._fallbackRetried = true;
      config.baseURL = devApiEndpoint;
      return apiClient.request(config);
    }
    if (error.response) {
      const apiError = parseApiError(error);
      const errorCode = apiError?.errorCode || apiError?.error_code;
      const detail = apiError?.detail || apiError?.error;

      if (error.response.status === 401) {
        logger.logWarning(`Unauthorized: ${errorCode || 'token may be invalid or expired'}`);
      } else if (error.response.status >= 500) {
        const logMessage = errorCode ? `Server error [${errorCode}]: ${detail ?? ''}` : `Server error: ${detail ?? ''}`;
        logger.logError(logMessage, error);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
